import { getMemberProjectDetails, MemberName, RemoveXgSeatList } from "@components/table/members/remove-xg-seat/remove-xg-seat-list";
import { getProjectChangeBlockReason, isProjectManager, PROJECTS_FAILED_MESSAGE } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { useChangeMemberProjects } from "@components/table/members/remove-xg-seat/use-change-member-projects";
import { useMemberEditorProjects } from "@components/table/members/remove-xg-seat/use-member-groups-with-details";
import { usePendingIds } from "@components/table/members/remove-xg-seat/use-pending-ids";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";

/**
 * Step of taking a Sphere XG seat away from a member that lists the projects in which the member
 * is an admin or an editor, and lets the user remove the member from them or make them a viewer
 * instead, one by one or all at once. The projects the member manages have a step of their own.
 */
export function RemoveXgSeatProjectsStep({
  member,
  onBusyChange,
  onResolvedChange
}) {
  const {
    projects,
    status
  } = useMemberEditorProjects(member.identity);
  const {
    pendingIds,
    runWhilePending
  } = usePendingIds(onBusyChange);
  const {
    changeProject
  } = useChangeMemberProjects({
    member
  });
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const editorProjects = projects.filter(({
    project
  }) => !isProjectManager(project));
  const changeableProjects = editorProjects.filter(({
    project: project_0
  }) => !getProjectChangeBlockReason(project_0, canAssignManagers));
  const name = <MemberName member={member} />;
  return <RemoveXgSeatList status={status} description={<>
          {name} is an admin or an editor in these projects. Remove {name} from
          them, or make {name} a viewer.
        </>} emptyMessage={<>{name} is not an admin or an editor in any project.</>} failedMessage={PROJECTS_FAILED_MESSAGE} items={editorProjects.map(toItem)} pendingIds={pendingIds} onResolvedChange={onResolvedChange} bulkActions={[{
    label: "Make viewer in all",
    onClick: () => void changeProjects(changeableProjects, "downgrade")
  }, {
    label: "Remove from all projects",
    variant: "destructiveGhost",
    onClick: () => void changeProjects(changeableProjects, "remove")
  }]} />;

  /** Returns how a project is listed */
  function toItem(memberProject) {
    return {
      id: memberProject.project.id,
      name: memberProject.project.name,
      details: getMemberProjectDetails(memberProject),
      blockReason: getProjectChangeBlockReason(memberProject.project, canAssignManagers),
      actions: [{
        label: "Make viewer",
        onClick: () => void changeProjects([memberProject], "downgrade")
      }, {
        label: "Remove",
        variant: "destructiveGhost",
        onClick: () => void changeProjects([memberProject], "remove")
      }]
    };
  }

  /**
   * Changes the role of the member in the projects one after the other, so that fetching a group
   * again after one of them cannot overwrite a change in another project of the same group. Each
   * project drops from the list unless the member still is an editor there through a team. A
   * project in which that failed stays listed, and the user is told why by the error toast of the
   * request.
   */
  async function changeProjects(projectsToChange, change) {
    await runWhilePending(projectsToChange.map(({
      project: project_1
    }) => project_1.id), async () => {
      for (const memberProject_0 of projectsToChange) {
        try {
          await changeProject(memberProject_0, change);
        } catch {
          // The error toast of the request tells the user
        }
      }
    });
  }
}